import React from 'react';
import { AppstoreOutlined, WarningOutlined } from '@ant-design/icons';
import { getDesignPresetNameParts } from 'common/design-tokens/preset-paths';
import t from 'utils/translate';
import DesignPresetTooltip from './DesignPresetTooltip';
import { getDesignPresetSummary } from './design-preset-summary';
import css from './DesignPresetSummary.scss';

type SummaryValue = ReturnType<typeof getDesignPresetSummary>['properties'][number]['values'][number];

function valueText(value: SummaryValue | undefined) {
	return value?.unresolved ? t('presets.unresolvedToken') : value?.value ?? '—';
}

export default function DesignPresetSummary({
	layout = 'stack',
	showPath = false,
	detailsEnabled = true,
	...context
}: Parameters<typeof getDesignPresetSummary>[0] & {
	layout?: 'row' | 'stack' | 'control';
	showPath?: boolean;
	detailsEnabled?: boolean;
}) {
	const summary = getDesignPresetSummary(context);
	const parts = getDesignPresetNameParts(context.preset.name);
	const visibleDetails = summary.details.slice(0, 2);
	const sizes = summary.sizes?.map(valueText).join(' / ');
	const sampleStyle = summary.isTypography
		? summary.sampleStyle
		: {
				backgroundColor: summary.backgroundColor === undefined ? undefined : String(summary.backgroundColor),
				borderColor: summary.borderColor === undefined ? undefined : String(summary.borderColor),
			};
	const content = (
		<span className={css.root} data-layout={layout}>
			<span className={css.sample} aria-hidden="true" style={sampleStyle}>
				{summary.isTypography ? 'Aa' : summary.backgroundColor === undefined && <AppstoreOutlined />}
			</span>
			<span className={css.content}>
				<span className={css.identity}>
					<strong className={css.name}>
						{parts.at(-1) ?? context.preset.name}
						{summary.unresolved && (
							<span className={css.warning} aria-label={t('presets.unresolvedToken')}>
								<WarningOutlined />
							</span>
						)}
					</strong>
					{showPath && parts.length > 1 && <span className={css.path}>{parts.slice(0, -1).join(' / ')}</span>}
				</span>
				{(summary.fontLabel || sizes) && (
					<span className={css.typography}>
						{summary.fontLabel && <span className={css.font}>{summary.fontLabel}</span>}
						{summary.fontLabel && sizes && <span aria-hidden="true">·</span>}
						{sizes && <span className={css.sizes}>{sizes}</span>}
					</span>
				)}
				{visibleDetails.length > 0 && (
					<span className={css.facts}>
						{visibleDetails.map(({ property, current }) => (
							<span className={css.detail} key={property}>
								{['color', 'backgroundColor', 'borderColor'].includes(property) &&
									current?.value !== undefined && (
										<span
											className={css.color}
											aria-hidden="true"
											style={{ backgroundColor: String(current.value) }}
										/>
									)}
								{t(`presets.properties.${property}`)} {valueText(current)}
							</span>
						))}
						{summary.details.length > visibleDetails.length && (
							<span>+{summary.details.length - visibleDetails.length}</span>
						)}
					</span>
				)}
				{!summary.properties.length && <span className={css.path}>{t('presets.emptyPreset')}</span>}
			</span>
		</span>
	);
	return detailsEnabled ? <DesignPresetTooltip {...context}>{content}</DesignPresetTooltip> : content;
}
